import {
  Issue,
  ProgramArea,
  SubmissionStatus,
  SubmissionStepInfo,
  SubmissionStepStatus,
  SubmissionStepType,
} from "generated-graphql/graphql";
import { useMemo } from "react";
import { isContactIssue, useFacility } from "routes/Customer/Facility/useFacility";
import { isRcraContactIssue } from "util/facilityContacts";

/**
 * Per-step display state for the waste submission stepper. Local to the waste
 * stepper — do NOT reuse TierII's ReportStepState; the underlying status enum
 * and rules differ.
 */
export enum SubmissionStepState {
  NotStarted = "not_started",
  InProgress = "in_progress",
  Done = "done",
  Invalid = "invalid",
}

/**
 * Pure derivation of a step's display state from its backend status plus
 * whether the compliance engine reported issues for it.
 *
 * - hasIssues → Invalid (overrides status)
 * - COMPLETED → Done
 * - IN_PROGRESS → InProgress
 * - NOT_STARTED / undefined → NotStarted
 *
 * VERIFY_AND_SUBMIT additionally derives Done from the submission's own status
 * in `useSubmissionStepStates` — see the special-casing there.
 */
export function getStepStatus({
  stepStatus,
  hasIssues,
}: {
  stepStatus?: SubmissionStepStatus;
  hasIssues: boolean;
}): SubmissionStepState {
  if (hasIssues) {
    return SubmissionStepState.Invalid;
  }
  if (stepStatus === SubmissionStepStatus.Completed) {
    return SubmissionStepState.Done;
  }
  if (stepStatus === SubmissionStepStatus.InProgress) {
    return SubmissionStepState.InProgress;
  }
  return SubmissionStepState.NotStarted;
}

function isRcraProfileIssue(issue: Issue): boolean {
  return (
    issue.modelName === "FacilityRcraProfile" ||
    (issue.modelName === "Facility" && issue.key === "facilityRcraProfile")
  );
}

function isStateFieldIssue(issue: Issue): boolean {
  return issue.modelName === "FacilityStateField";
}

/**
 * Buckets the facility-level compliance issues by submission step.
 *
 * The stepper route has no `:facilityId` path param, so unlike the param-based
 * facility issue hooks we must source issues from `useFacility(facilityId)`
 * explicitly. The per-step predicates mirror `useRcraProfileIssues`,
 * `useContactIssues`, and `useStateInformationIssues` from `useFacility`.
 *
 * Steps that are review-only or fully derived (generator status, waste streams,
 * overview, review, verify & submit) have no directly attributable issues.
 */
export function useSubmissionIssuesGroupedByStep(
  facilityId: string
): Partial<Record<SubmissionStepType, number>> {
  const { data } = useFacility(facilityId);
  const issues = data?.facility?.issues;
  const stateFields = data?.facility?.stateFields;

  return useMemo(() => {
    const facilityIssues = issues ?? [];
    // The waste stepper is RCRA-only. State-field issues are keyed by the field
    // `key`, so an issue counts for this step only when its key belongs to an
    // RCRA (waste) field — EPCRA/chemical state-field gaps are excluded.
    const rcraStateFieldKeys = new Set(
      (stateFields ?? []).filter((f) => f.type === ProgramArea.Rcra).map((f) => f.key)
    );
    return {
      [SubmissionStepType.FacilityProfile]: facilityIssues.filter(isRcraProfileIssue).length,
      // Mirrors `ContactsStep`/`WasteContacts`: a contact issue counts only when
      // it is also RCRA-scoped (`isRcraContactIssue`), excluding Tier II/EPCRA
      // contact gaps that the step itself never surfaces.
      [SubmissionStepType.Contacts]: facilityIssues.filter(
        (issue) => isContactIssue(issue) && isRcraContactIssue(issue)
      ).length,
      [SubmissionStepType.StateFields]: facilityIssues.filter(
        (issue) =>
          isStateFieldIssue(issue) && issue.key != null && rcraStateFieldKeys.has(issue.key)
      ).length,
    };
  }, [issues, stateFields]);
}

/**
 * Combines the backend-supplied `stepperSteps` (already ordered and filtered to
 * applicable steps) with the grouped compliance issues to produce a display
 * state per step. Only steps present in `stepperSteps` are included.
 *
 * VERIFY_AND_SUBMIT is special-cased on `submissionStatus`: a submission only
 * reaches PROCESSING_SUBMISSION / FILING_COMPLETE through verify & submit, so
 * the step is definitionally done once frozen. Its persisted SubmissionStep row
 * can't be trusted for this — the row is only written by a client-side touch
 * right after the verify click, so it stays NOT_STARTED when the transition
 * happened anywhere else (and the frozen stepper offers no way to re-touch it).
 */
export function useSubmissionStepStates(
  stepperSteps: Pick<SubmissionStepInfo, "step" | "status">[],
  facilityId: string,
  submissionStatus?: SubmissionStatus | null
): Partial<Record<SubmissionStepType, SubmissionStepState>> {
  const issuesByStep = useSubmissionIssuesGroupedByStep(facilityId);

  return useMemo(() => {
    // Deliberately narrower than `isSubmissionFrozen`: NOT_REPORTING also
    // freezes a submission, but it was never verified & submitted.
    const hasBeenSubmitted =
      submissionStatus === SubmissionStatus.ProcessingSubmission ||
      submissionStatus === SubmissionStatus.FilingComplete;
    const states: Partial<Record<SubmissionStepType, SubmissionStepState>> = {};
    for (const { step, status } of stepperSteps) {
      if (step === SubmissionStepType.VerifyAndSubmit && hasBeenSubmitted) {
        states[step] = SubmissionStepState.Done;
        continue;
      }
      states[step] = getStepStatus({
        stepStatus: status,
        hasIssues: (issuesByStep[step] ?? 0) > 0,
      });
    }
    return states;
  }, [stepperSteps, issuesByStep, submissionStatus]);
}
